/**
 * Selection Screen V2 Mappings
 *
 * Mappings for selection screens with OptionsList:
 * - ListMultiPick, ListSinglePick
 * - GridMultiPick, GridSinglePick
 * - HeroList
 *
 * Targets: content.options.items[].title.appearance.*, content.options.appearance.*
 *
 * @module themes/mappers/mappingTablesV2
 */

import type { ThemeV2Mapping } from '../ThemeV2Mapper';
import type { StepKind } from '@segmently/schema-models/v2';
import { addAlpha50 } from './copyBlockMappings';

// ============================================================================
// StepKind Helpers
// ============================================================================

/** StepKinds that have options list */
const hasOptions = (stepKind: StepKind): boolean =>
  stepKind === 'ListMultiPick' ||
  stepKind === 'ListSinglePick' ||
  stepKind === 'GridMultiPick' ||
  stepKind === 'GridSinglePick' ||
  stepKind === 'HeroList';

const isSingleSelection = (stepKind: StepKind): boolean =>
  stepKind === 'ListSinglePick' || stepKind === 'GridSinglePick';

const toTrue = () => true;

/** EdgeInsets are written to two targets — clone so they never share a reference. */
const cloneInsets = (value: unknown) =>
  value && typeof value === 'object' ? { ...(value as Record<string, unknown>) } : value;

// ============================================================================
// Selection Mappings
// ============================================================================

/**
 * Selection screen mappings for options items and list appearance.
 *
 * V2 paths:
 * - Item title color: options.items[].title.appearance.color
 * - Item subtitle color: options.items[].subtitle.appearance.color
 * - Item title font: options.items[].title.appearance.fontFamily/fontSize/fontWeight
 * - Item subtitle font: options.items[].subtitle.appearance.fontFamily/fontSize/fontWeight
 * - Checkbox kind: options.items[].checkbox.kind
 * - List border radius: options.appearance.borders.radius.uniform
 */
export const SELECTION_V2_MAPPINGS: ThemeV2Mapping[] = [
  // === Item Title Color ===
  {
    source: 'colors.titlesSubtitles',
    target: 'options.items[].title.appearance.color',
    condition: hasOptions,
  },

  // === Item Subtitle Color (50% alpha) ===
  {
    source: 'colors.titlesSubtitles',
    target: 'options.items[].subtitle.appearance.color',
    transform: addAlpha50,
    condition: hasOptions,
  },

  // === Item Title Font Properties ===
  {
    source: 'optionsListSettings.itemTitle.fontFamily',
    target: 'options.items[].title.appearance.fontFamily',
    condition: hasOptions,
  },
  {
    source: 'optionsListSettings.itemTitle.fontSize',
    target: 'options.items[].title.appearance.fontSize',
    condition: hasOptions,
  },
  {
    source: 'optionsListSettings.itemTitle.fontWeight',
    target: 'options.items[].title.appearance.fontWeight',
    condition: hasOptions,
  },

  // === Item Subtitle Font Properties ===
  {
    source: 'optionsListSettings.itemSubtitle.fontFamily',
    target: 'options.items[].subtitle.appearance.fontFamily',
    condition: hasOptions,
  },
  {
    source: 'optionsListSettings.itemSubtitle.fontSize',
    target: 'options.items[].subtitle.appearance.fontSize',
    condition: hasOptions,
  },
  {
    source: 'optionsListSettings.itemSubtitle.fontWeight',
    target: 'options.items[].subtitle.appearance.fontWeight',
    condition: hasOptions,
  },

  // === Checkbox Kind ===
  {
    source: 'optionsListSettings.checkboxKind',
    target: 'options.items[].checkbox.kind',
    condition: hasOptions,
  },

  // === Checkbox and option accent colors ===
  {
    source: 'colors.titlesSubtitles',
    target: 'options.items[].checkbox.appearance.color',
    transform: addAlpha50,
    condition: hasOptions,
  },
  {
    source: 'colors.buttonsBackground',
    target: 'options.items[].checkbox.selectedAppearance.color',
    condition: hasOptions,
  },
  {
    source: 'colors.buttonsBackground',
    target: 'options.items[].checkbox.selectedAppearance.isBackgroundFilled',
    transform: toTrue,
    condition: hasOptions,
  },
  // Border widths are owned by the screen's per-state settings (editor "Unselected/
  // Selected item state" → borders.width) — theme apply must not overwrite them.
  {
    source: 'colors.buttonsBackground',
    target: 'options.appearance.borders.color',
    condition: hasOptions,
  },
  {
    source: 'colors.buttonsBackground',
    target: 'options.selectedAppearance.borders.color',
    condition: hasOptions,
  },

  // === Single-selection options follow the primary button color rule ===
  {
    source: 'colors.buttonsBackground',
    target: 'options.appearance.backgroundColor',
    condition: isSingleSelection,
  },
  {
    source: 'colors.buttonsBackground',
    target: 'options.selectedAppearance.backgroundColor',
    condition: isSingleSelection,
  },
  {
    source: 'colors.buttonsTitle',
    target: 'options.items[].title.appearance.color',
    condition: isSingleSelection,
  },
  {
    source: 'colors.buttonsTitle',
    target: 'options.items[].subtitle.appearance.color',
    transform: addAlpha50,
    condition: isSingleSelection,
  },
  {
    source: 'colors.buttonsTitle',
    target: 'options.items[].checkbox.appearance.color',
    condition: isSingleSelection,
  },

  // === Granular item colors (last-defined override over palette rules) ===
  // Subtitle color is already authored with the intended alpha in the theme;
  // do not transform it a second time.
  {
    source: 'optionsListSettings.itemTitle.color',
    target: 'options.items[].title.appearance.color',
    condition: hasOptions,
  },
  {
    source: 'optionsListSettings.itemSubtitle.color',
    target: 'options.items[].subtitle.appearance.color',
    condition: hasOptions,
  },

  // === List Border Radius ===
  {
    source: 'optionsListSettings.listBorderRadius',
    target: 'options.appearance.borders.radius.uniform',
    condition: hasOptions,
  },

  // === Layout settings: renderer-backed list/grid geometry ===
  {
    source: 'layoutSettings.optionsList.itemRadius',
    target: 'options.appearance.borders.radius.uniform',
    condition: hasOptions,
  },
  // Item padding is a single setting, not per-state: write the same insets to both
  // appearance and selectedAppearance so a selected cell never changes height.
  {
    source: 'layoutSettings.optionsList.itemInsets',
    target: 'options.appearance.insets',
    transform: cloneInsets,
    condition: hasOptions,
  },
  {
    source: 'layoutSettings.optionsList.itemInsets',
    target: 'options.selectedAppearance.insets',
    transform: cloneInsets,
    condition: hasOptions,
  },
  {
    source: 'layoutSettings.optionsList.layoutInsets',
    target: 'options.layout.insets',
    condition: hasOptions,
  },
  {
    source: 'layoutSettings.optionsList.itemsSpacing',
    target: 'options.appearance.itemsSpacing',
    condition: hasOptions,
  },
  {
    source: 'layoutSettings.optionsList.cellHeightFromCellWidth',
    target: 'options.cellHeightFromCellWidth',
    condition: hasOptions,
  },
  {
    source: 'layoutSettings.optionsList.useMaxCellHeight',
    target: 'options.appearance.useMaxCellHeight',
    condition: hasOptions,
  },

  // === Main fontFamily cascade to items ===
  {
    source: 'fonts.main.fontFamily',
    target: 'options.items[].title.appearance.fontFamily',
    condition: hasOptions,
  },
  {
    source: 'fonts.main.fontFamily',
    target: 'options.items[].subtitle.appearance.fontFamily',
    condition: hasOptions,
  },
];
